import React, { useState, useEffect } from "react";
import {
  Table, Button, Select, DatePicker, message, Tag, Input, Dropdown, Space, Tooltip, Pagination
} from "antd";
import {
  UserOutlined, DollarOutlined, WalletOutlined, HeartOutlined, CrownOutlined,
  FileExcelOutlined, FilePdfOutlined, PrinterOutlined, SearchOutlined, ReloadOutlined,
  DownOutlined, UpOutlined, BarChartOutlined, RiseOutlined, FallOutlined,
  TeamOutlined, TransactionOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import {
  UserGet, UserDataGet, UserTransactionsGet, StaffTransactionsGet,
  DevTransactionsGet, AdminTotalRevenueGet,
} from "../../../../Api/CoreApi";
import { exportToCSV, REPORT_COLUMNS } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import { REPORT_TITLES } from "../../Utils/constants";
import "./Reports.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

function Reports() {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState("users");
  const [dateRange, setDateRange] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [stats, setStats] = useState({
    totalUsers: 0, totalStaff: 0, totalTransactions: 0, totalRevenue: 0,
    totalWithdrawals: 0, activeUsers: 0, premiumUsers: 0, pendingWithdrawals: 0,
  });

  const [allData, setAllData] = useState({
    users: [], staff: [], allTx: [], adminRevenue: [], withdrawals: [],
  });
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 📱 Mobile Responsiveness States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileStats, setShowMobileStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const admin_id = localStorage.getItem("user_id");

  const getProfileId = (item) => {
    if (!item) return 'N/A';
    if (item.profile_id) return item.profile_id;
    if (item.user?.profile_id) return item.user.profile_id;
    if (item.UserData?.profile_id) return item.UserData.profile_id;
    if (item.role === 'staff') return `CRST${String(item.id || 1000).padStart(6, '0')}`;
    if (item.role === 'user' || !item.role) return `CR${String(item.id || item.user_id || 1000).padStart(6, '0')}`;
    return `ID${String(item.id || 1000).padStart(6, '0')}`;
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const [usersRes, userDataRes, userTxRes, staffTxRes, devTxRes, revenueRes] =
        await Promise.all([
          UserGet(), UserDataGet(), UserTransactionsGet(),
          StaffTransactionsGet(), DevTransactionsGet(), AdminTotalRevenueGet(),
        ]);

      const allUsers = Array.isArray(usersRes) ? usersRes : [];
      const userDataList = Array.isArray(userDataRes) ? userDataRes : [];

      const userDataMap = {};
      userDataList.forEach(ud => { userDataMap[ud.user_id || ud.user] = ud; });

      const mergedUsers = allUsers.map(user => {
        const userData = userDataMap[user.id];
        return {
          ...user,
          premium: userData?.premium || false,
          is_premium: userData?.premium || false,
          is_active: userData?.is_active !== undefined ? userData.is_active : user.is_active,
          profile_id: userData?.profile_id || user.profile_id,
          first_name: userData?.firstname || user.first_name,
          contact: userData?.contact || user.contact,
          email: userData?.email || user.email,
          gender: userData?.gender || user.gender,
          age: userData?.age || null,
          city: userData?.city || '',
          state: userData?.state || '',
          balance: user.balance || 0,
          religion: userData?.religion || '',
          caste: userData?.caste || '',
        };
      });

      const users = mergedUsers.filter((u) => u.role === "user");
      const staff = mergedUsers.filter((u) => u.role === "staff");

      const userTx = Array.isArray(userTxRes) ? userTxRes : [];
      const staffTx = Array.isArray(staffTxRes) ? staffTxRes : [];
      const devTx = Array.isArray(devTxRes) ? devTxRes : [];
      const allTx = [...userTx, ...staffTx, ...devTx];

      const revenue = Array.isArray(revenueRes) ? revenueRes : [];
      const adminRevenue = revenue.filter((r) => String(r.admin_id) === String(admin_id));
      const totalRevenueAmount = adminRevenue.reduce((sum, r) => sum + (parseFloat(r.admin_amount) || 0), 0);

      const withdrawals = allTx.filter((t) => t.type === "withdrawal" || t.type === "debit");
      const totalWithdrawalAmount = withdrawals.reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0);
      const pendingW = withdrawals.filter((w) => w.status === "pending").length;

      const premiumUsers = users.filter((u) => u.premium || u.is_premium).length;

      setStats({
        totalUsers: users.length, totalStaff: staff.length, totalTransactions: allTx.length,
        totalRevenue: totalRevenueAmount, totalWithdrawals: totalWithdrawalAmount,
        activeUsers: users.filter((u) => u.is_active).length, premiumUsers: premiumUsers,
        pendingWithdrawals: pendingW,
      });

      setAllData({ users, staff, allTx, adminRevenue, withdrawals });
      updateReportData(reportType, { users, staff, allTx, adminRevenue, withdrawals });

    } catch (error) {
      message.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const updateReportData = (type, data) => {
    let filtered = [];
    if (type === "users") filtered = data.users || [];
    else if (type === "staff") filtered = data.staff || [];
    else if (type === "transactions") filtered = data.allTx || [];
    else if (type === "revenue") filtered = data.adminRevenue || [];
    else if (type === "withdrawals") filtered = data.withdrawals || [];
    setReportData(filtered);
    setFilteredData(filtered);
  };

  useEffect(() => { fetchReportData(); }, []);

  useEffect(() => {
    updateReportData(reportType, allData);
    setCurrentPage(1);
  }, [reportType]);

  useEffect(() => {
    let filtered = [...reportData];
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((item) => {
        const profileId = getProfileId(item).toLowerCase();
        const jsonStr = JSON.stringify(item).toLowerCase();
        return jsonStr.includes(searchLower) || profileId.includes(searchLower);
      });
    }
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.created_date || item.date);
        return itemDate >= start.toDate() && itemDate <= end.toDate();
      });
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchText, dateRange, reportData]);

  const handleExportCSV = (exportAll = false) => {
    try {
      const dataToExport = exportAll ? reportData : filteredData;
      exportToCSV(dataToExport, REPORT_COLUMNS[reportType], REPORT_TITLES[reportType].filename);
    } catch (error) { message.error("Failed to export CSV"); }
  };

  const handleExportPDF = (exportAll = false) => {
    try {
      const dataToExport = exportAll ? reportData : filteredData;
      const config = REPORT_TITLES[reportType];
      exportToPDF(dataToExport, REPORT_COLUMNS[reportType], { title: config.title, subtitle: config.subtitle, filename: config.filename });
    } catch (error) { message.error("Failed to export PDF"); }
  };

  const handlePrint = () => window.print();

  const csvMenuItems = {
    items: [
      { key: "csv-filtered", label: `Filtered (${filteredData.length})`, onClick: () => handleExportCSV(false) },
      { key: "csv-all", label: `All (${reportData.length})`, onClick: () => handleExportCSV(true) },
    ],
  };

  const pdfMenuItems = {
    items: [
      { key: "pdf-filtered", label: `Filtered (${filteredData.length})`, onClick: () => handleExportPDF(false) },
      { key: "pdf-all", label: `All (${reportData.length})`, onClick: () => handleExportPDF(true) },
    ],
  };

  const getDisplayColumns = () => {
    const cols = REPORT_COLUMNS[reportType] || [];
    return cols.map((col) => ({
      title: col.label,
      key: col.key,
      width: col.key === "sno" ? 60 : col.key === "profile_id" || col.key === "user_ref" || col.key === "admin_ref" ? 120 : undefined,
      render: (_, record, index) => {
        if (col.key === "sno") return (currentPage - 1) * pageSize + index + 1;
        if (col.key === "profile_id" || col.key === "user_ref" || col.key === "admin_ref") {
          const value = col.format ? col.format(record) : record[col.key];
          return <span className="profile-id-cell">{value}</span>;
        }
        if (col.format) {
          const value = col.format(record);
          if (col.key === "status") {
            const color = value === "Active" || value === "success" || value === "approved" ? "green" : value === "pending" ? "orange" : "red";
            return <Tag color={color}>{value}</Tag>;
          }
          if (col.key.includes("amount")) return <span className="admin-reports-amount-text">₹{value}</span>;
          return value;
        }
        return record[col.key] || "N/A";
      },
    }));
  };

  // ✅ APP-LIKE MOBILE CARD RENDERER
  const renderMobileCard = (record, index) => {
    const cols = REPORT_COLUMNS[reportType] || [];
    const mainField = cols.find(c => c.key === 'profile_id' || c.key === 'user_ref' || c.key === 'admin_ref');
    const titleField = cols.find(c => c.key === 'name' || c.key === 'username' || c.key === 'type');
    const statusField = cols.find(c => c.key === 'status');
    const otherFields = cols.filter(c => c.key !== 'sno' && c !== mainField && c !== titleField && c !== statusField);
    
    const statusValue = statusField && statusField.format ? statusField.format(record) : record[statusField?.key];
    const isSuccess = statusValue === "Active" || statusValue === "success" || statusValue === "approved";
    const isPending = statusValue === "pending";

    return (
      <div key={record.id || index} className="mobile-report-card">
        <div className="mrc-header">
          <span className="mrc-id">
            {mainField ? (mainField.format ? mainField.format(record) : record[mainField.key]) : `ID: ${record.id}`}
          </span>
          {statusField && (
            <span className={`mrc-status ${isSuccess ? 'success' : isPending ? 'warning' : 'error'}`}>
              {statusValue}
            </span>
          )}
        </div>
        
        <div className="mrc-body">
          <div className="mrc-avatar">
            {reportType === 'transactions' || reportType === 'withdrawals' || reportType === 'revenue' ? <DollarOutlined /> : <UserOutlined />}
          </div>
          <div className="mrc-info">
            <h4 className="mrc-title">{titleField ? (titleField.format ? titleField.format(record) : record[titleField.key]) : "Report Item"}</h4>
          </div>
        </div>

        <div className="mrc-details-grid">
          {otherFields.slice(0, 4).map((col, i) => (
            <div className="mrc-detail-item" key={i}>
              <span className="mrc-label">{col.label}</span>
              <span className={`mrc-value ${col.key.includes('amount') ? 'amount' : ''}`}>
                {col.key.includes('amount') ? '₹' : ''}
                {col.format ? col.format(record) : (record[col.key] || 'N/A')}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-reports-page">
      {/* ============================================
          PAGE HEADER (COMPACT)
          ============================================ */}
      <div className="header-stats-wrapper">
        <div className="admin-reports-page-header">
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><BarChartOutlined /></div>
              <div>
                <h2 className="page-title">Reports</h2>
                <p className="page-subtitle">Generate & view detailed analytics</p>
              </div>
            </div>
          </div>

          <div className="header-actions">
            <Tooltip title="Print">
              <Button icon={<PrinterOutlined />} onClick={handlePrint} className="icon-btn" />
            </Tooltip>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchReportData} loading={loading} className="icon-btn" />
            </Tooltip>
            <Dropdown menu={csvMenuItems}>
              <Button icon={<FileExcelOutlined />} className="csv-btn"><span>CSV</span></Button>
            </Dropdown>
            <Dropdown menu={pdfMenuItems}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn"><span>PDF</span></Button>
            </Dropdown>

            {/* Mobile Only: Stats Toggle (Arrow) */}
            {isMobile && (
              <button
                type="button"
                className="mobile-stats-icon-btn"
                onClick={() => setShowMobileStats(p => !p)}
              >
                {showMobileStats ? <UpOutlined /> : <DownOutlined />}
              </button>
            )}
          </div>
        </div>

        {/* Animated Smooth Stats Accordion */}
        <div className={`mini-stats-bar ${isMobile && showMobileStats ? 'show' : ''}`}>
          <div className="mini-stat total">
            <UserOutlined />
            <div className="stat-content">
              <b>{stats.totalUsers}</b> <span>Total Users</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat active">
            <CrownOutlined />
            <div className="stat-content">
              <b>{stats.premiumUsers}</b> <span>Premium Users</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat revenue">
            <DollarOutlined />
            <div className="stat-content">
              <b>₹{stats.totalRevenue.toLocaleString("en-IN")}</b> <span>Revenue</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat withdrawals">
            <WalletOutlined />
            <div className="stat-content">
              <b>₹{stats.totalWithdrawals.toLocaleString("en-IN")}</b> <span>Withdrawals</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat">
            <TeamOutlined style={{ color: '#722ed1' }} />
            <div className="stat-content">
              <b style={{ color: '#722ed1' }}>{stats.totalStaff}</b> <span>Total Staff</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat">
            <TransactionOutlined style={{ color: '#1890ff' }} />
            <div className="stat-content">
              <b style={{ color: '#1890ff' }}>{stats.totalTransactions}</b> <span>Transactions</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================
          FILTERS
          ============================================ */}
      <div className="admin-reports-filters-card">
        <div className="admin-reports-filters-row">
          <div className="admin-reports-filter-group admin-reports-filter-search">
            <Input
              placeholder="Search by name, ID..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="admin-reports-search"
              allowClear
            />
          </div>

          <div className="admin-reports-filter-group admin-reports-filter-type">
            <Select
              value={reportType}
              onChange={setReportType}
              className="admin-reports-select"
            >
              <Option value="users">👥 Users Report</Option>
              <Option value="staff">👤 Staff Report</Option>
              <Option value="transactions">💳 Transactions</Option>
              <Option value="revenue">💰 Revenue Report</Option>
              <Option value="withdrawals">💸 Withdrawals</Option>
            </Select>
          </div>

          <div className="admin-reports-filter-group admin-reports-filter-date">
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              className="admin-reports-date-picker"
              placeholder={['Start date', 'End date']}
            />
          </div>

          <div className="admin-reports-filter-group admin-reports-filter-clear">
            <Button
              className="admin-reports-clear-filters-btn"
              onClick={() => { setSearchText(""); setDateRange(null); }}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* ============================================
          REPORT TABLE / CARDS
          ============================================ */}
      <div className="admin-reports-table-card">
        <div className="admin-reports-table-header">
          <div className="admin-reports-table-header-left">
            <h3>{REPORT_TITLES[reportType]?.title}</h3>
            <p className="desktop-only-text">{REPORT_TITLES[reportType]?.subtitle}</p>
          </div>
          <div className="admin-reports-table-header-right">
            <Tag className="admin-reports-count-tag admin-reports-count-total">Total: {reportData.length}</Tag>
            {filteredData.length !== reportData.length && (
               <Tag className="admin-reports-count-tag admin-reports-count-filtered">Filtered: {filteredData.length}</Tag>
            )}
          </div>
        </div>

        {isMobile ? (
          <div className="mobile-report-list">
            {filteredData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No records found</div>
            ) : (
              filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((r, i) => renderMobileCard(r, i))
            )}
            
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredData.length}
              onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
              size="small"
              style={{ textAlign: 'center', marginTop: 16 }}
            />
          </div>
        ) : (
          <Table
            rowKey={(record, index) => record.id || index}
            columns={getDisplayColumns()}
            dataSource={filteredData}
            loading={loading}
            size="middle"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total, range) => <span>Showing <b>{range[0]}-{range[1]}</b> of <b>{total}</b> records</span>,
              onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            }}
            scroll={{ x: 1000 }}
            className="admin-reports-table"
          />
        )}
      </div>
    </div>
  );
}

export default Reports;